import {
    ColumnType,
    FileUploadRes,
    MediaType,
    FieldOperator,
    FilterType,
    LogicalOperator,
    YascOptionItem,
    YascSectionOrderStrategy,
    yascClassDisplayName,
    yascClassDescription,
    yascClassFormLayout,
    yascClassDefaultFilters,
    yascClassDefaultSorting,
    yascClassFormSectionTitle,
    yascClassSectionOrderStrategy,
    yascCollection,
    yascDisplayName,
    yascDescription,
    yascEntityName,
    yascEnum,
    yascField,
    yascFileUpload,
    yascFilterConfig,
    yascFormGrid,
    yascFormIndex,
    yascFormIgnore,
    yascFormKind,
    yascFormRows,
    yascFormSubsection,
    yascFormSubsectionDescription,
    yascMax,
    yascMaxLength,
    yascMin,
    yascNested,
    yascOptionsKey,
    yascRequired,
    yascSectionOrder,
    yascTableColumn,
    yascTableDisplayFn,
    yascTableSuffix,
    yascTableFilterType,
    yascTableHidden,
    yascTableIgnore,
    yascTableIndex,
    yascTableLevelOfDetail,
    yascTableWidth,
    yascFormTooltip,
    yascVisibleWhen,
} from '@yasc/yasc-material-components';
import {CompanyMinimumRes} from '../cp';
import {WorkflowStatusMinRes} from './status.contract';
import {BudgetCapacityInterval, BudgetCapacityUnit, BudgetCarryOverType, BudgetOveragePolicy, BudgetPricingModel, BudgetRecurrenceType, ProjectRole} from '../../../enums';
import {InvoiceDocumentFormat} from '../../../enums/invoice-document-format';
import {TimesheetTemplate} from '../../../enums/timesheet-template';
import {TimesheetSignee} from '../../../enums/timesheet-signee';
import {ProjectContractStatus} from '../../../enums/project-contract';
import {BaseTaskRes} from './task';
import {InvoiceDetailRes} from '../cp/invoice.contract';
import {ProjectBudgetRes} from './project.contract';

const PROJECT_ACTIVE_STATUS_NAME = 'Auftrag (aktiv)';

function formatCurrency(value?: number | null): string {
    if (value === undefined || value === null) return '';
    return new Intl.NumberFormat('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value);
}

function displayCompany(value?: CompanyMinimumRes | number | null): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return `${value}`;
    return value.name ?? '';
}

function displayWorkflowStatus(value?: WorkflowStatusMinRes | null): string {
    return value?.name ?? '';
}

@yascClassDisplayName('projectTabTeamTitle')
@yascClassFormSectionTitle('projectTabTeamTitle')
export class ProjectTeamMember {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;

    @yascField()
    @yascDisplayName('general_employee')
    @yascTableIndex(0)
    @yascTableWidth(240)
    @yascTableColumn(ColumnType.Autocomplete)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectTeamUsers')
    @yascRequired()
    @yascFormGrid({row: 1, width: 2, fullRow: true})
    userId: number | null = null;

    @yascField()
    @yascDisplayName('general_role')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Select)
    @yascFormKind('select')
    @yascOptionsKey('projectRoles')
    @yascRequired()
    @yascFormGrid({row: 2, width: 2, fullRow: true})
    roleId: ProjectRole | null = null;

    user?: {id?: number; givenName?: string; lastName?: string; userName?: string; email?: string; fullName?: string};
    userName?: string;

    constructor(init: Partial<ProjectTeamMember> = {}) {
        Object.assign(this, init);
        this.userId = init.userId ?? init.user?.id ?? null;
        this.roleId = init.roleId ?? null;
        this.userName = init.userName ?? (
            [init.user?.givenName, init.user?.lastName].filter(Boolean).join(' ').trim()
            || init.user?.fullName
            || init.user?.email
        );
    }
}

@yascClassDisplayName('projectContractsTitle')
@yascClassFormSectionTitle('projectContractsTitle')
@yascClassFormLayout({columns: 2, gap: '12px'})
@yascClassDescription('projectContractsDescription')
export class ProjectContractItem {
    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    clientId = globalThis.crypto?.randomUUID?.() ?? `contract-${Date.now()}-${Math.random()}`;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;


    @yascDisplayName('projectContractNumber')
    @yascDescription('projectContractNumberDescription')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(150)
    @yascRequired()
    @yascMaxLength(100)
    @yascFormSubsection('projectContractSectionGeneral')
    @yascFormGrid({row: 1, width: 1})
    contractNumber = '';

    @yascField()
    @yascDisplayName('general_name')
    @yascDescription('projectContractNameDescription')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(220)
    @yascRequired()
    @yascMaxLength(150)
    @yascFormSubsection('projectContractSectionGeneral')
    @yascFormGrid({row: 1, width: 1})
    name = '';

    @yascField()
    @yascDisplayName('general_status')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Select)
    @yascEnum(ProjectContractStatus, 'projectContractStatusEnum')
    @yascFormKind('select')
    @yascRequired()
    @yascFormSubsection('projectContractSectionGeneral')
    @yascFormGrid({row: 2, width: 1})
    status: ProjectContractStatus = ProjectContractStatus.Draft;

    @yascField()
    @yascDisplayName('projectContractBudgetCount')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Number)
    @yascFormIgnore()
    budgetCount = 0;

    @yascField()
    @yascDisplayName('projectContractStart')
    @yascDescription('projectContractStartDescription')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascRequired()
    @yascFormSubsection('projectContractSectionTerm')
    @yascFormGrid({row: 1, width: 1})
    start?: string;

    @yascField()
    @yascDisplayName('projectContractEnd')
    @yascDescription('projectContractEndDescription')
    @yascTableIndex(5)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormSubsection('projectContractSectionTerm')
    @yascFormGrid({row: 1, width: 1})
    end?: string;

    @yascField()
    @yascDisplayName('projectContractDocument')
    @yascDescription('projectContractDocumentDescription')
    @yascTableIgnore(true)
    @yascFileUpload({mediaType: MediaType.Document, allowMultiUpload: false, maxFileSizeMb: 25})
    @yascFormSubsection('projectContractSectionDocument')
    @yascFormGrid({row: 1, width: 2, fullRow: true})
    document: FileUploadRes | null = null;

    @yascField()
    @yascDisplayName('general_description')
    @yascTableIgnore(true)
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascMaxLength(1000)
    @yascFormSubsection('projectContractSectionAdditional')
    @yascFormGrid({row: 1, width: 2, fullRow: true})
    comment = '';

    constructor(init: Partial<ProjectContractItem> = {}) {
        Object.assign(this, init);
        this.clientId = init.clientId ?? this.clientId;
        this.document = init.document ?? null;
    }
}

function budgetUsesModel(draft: any, ...models: BudgetPricingModel[]): boolean {
    return models.includes(draft?.pricingModel ?? BudgetPricingModel.TimeAndMaterial);
}

function budgetSupportsRecurrence(draft: any): boolean {
    return budgetUsesModel(draft, BudgetPricingModel.PrepaidHours, BudgetPricingModel.Retainer);
}

function budgetHasCapacityInterval(draft: any): boolean {
    return (draft?.capacityInterval ?? BudgetCapacityInterval.None) !== BudgetCapacityInterval.None;
}

function budgetSupportsOverage(draft: any): boolean {
    if (budgetUsesModel(draft, BudgetPricingModel.CappedTimeAndMaterial, BudgetPricingModel.PrepaidHours))
        return true;

    return budgetUsesModel(draft, BudgetPricingModel.Retainer);
}

function budgetPricingModelDescriptionKey(draft: any): string {
    switch (draft?.pricingModel ?? BudgetPricingModel.TimeAndMaterial) {
        case BudgetPricingModel.FixedPrice:
            return 'projectBudgetPricingModelDescriptionEnum.FixedPrice';
        case BudgetPricingModel.CappedTimeAndMaterial:
            return 'projectBudgetPricingModelDescriptionEnum.CappedTimeAndMaterial';
        case BudgetPricingModel.PrepaidHours:
            return 'projectBudgetPricingModelDescriptionEnum.PrepaidHours';
        case BudgetPricingModel.Retainer:
            return 'projectBudgetPricingModelDescriptionEnum.Retainer';
        case BudgetPricingModel.TimeAndMaterial:
        default:
            return 'projectBudgetPricingModelDescriptionEnum.TimeAndMaterial';
    }
}

@yascClassDisplayName('projectBudgetTitle')
@yascClassFormSectionTitle('projectBudgetTitle')
@yascClassFormLayout({columns: 2, gap: '12px'})
@yascClassDescription('projectBudgetFormDescription')
export class ProjectBudgetItem {
    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    clientId = globalThis.crypto?.randomUUID?.() ?? `budget-${Date.now()}-${Math.random()}`;

    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;

    @yascField()
    @yascDisplayName('general_active')
    @yascTableIndex(5)
    @yascTableColumn(ColumnType.CheckBox)
    @yascFormKind('checkbox')
    @yascFormIndex(0)
    @yascFormGrid({width: 2, fullRow: true})
    isActive = true;

    @yascField()
    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(140)
    @yascRequired()
    @yascMaxLength(150)
    @yascFormSubsection('projectBudgetSectionAssignment')
    @yascFormIndex(10)
    @yascFormGrid({width: 1})
    name = '';

    @yascField()
    @yascDisplayName('projectBudgetEmployee')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(150)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectBudgetUsers')
    @yascFormSubsection('projectBudgetSectionAssignment')
    @yascFormIndex(11)
    @yascFormGrid({width: 1})
    userId: number | null = null;

    @yascField()
    @yascDisplayName('generalOrderId')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    @yascFormSubsection('projectBudgetSectionAssignment')
    @yascFormIndex(12)
    @yascFormGrid({width: 1})
    orderNumber = '';

    @yascField()
    @yascDisplayName('projectBudgetSourceOffer')
    @yascTableIgnore(true)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectOffers')
    @yascFormSubsection('projectBudgetSectionAssignment')
    @yascFormIndex(13)
    @yascFormGrid({width: 1})
    sourceOfferId: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetContract')
    @yascTableIgnore(true)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectContracts')
    @yascFormSubsection('projectBudgetSectionAssignment')
    @yascFormIndex(14)
    @yascFormGrid({width: 2, fullRow: true})
    contractId: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetContract')
    @yascTableIndex(6)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(160)
    @yascTableHidden(true)
    @yascFormIgnore()
    contractNumber = '';

    @yascField()
    @yascDisplayName('projectBudgetPricingModel')
    @yascEnum(BudgetPricingModel, 'projectBudgetPricingModelEnum')
    @yascTableIndex(2)
    @yascTableWidth(180)
    @yascFormKind('select')
    @yascRequired()
    @yascFormTooltip('projectBudgetPricingModelHint')
    @yascFormSubsection('projectBudgetSectionBillingModel')
    @yascFormSubsectionDescription(({draft}) => budgetPricingModelDescriptionKey(draft))
    @yascFormIndex(20)
    @yascFormGrid({width: 2, fullRow: true})
    pricingModel: BudgetPricingModel = BudgetPricingModel.TimeAndMaterial;

    @yascField()
    @yascDisplayName('projectBudgetCurrencyCode')
    @yascTableIgnore(true)
    @yascRequired()
    @yascMaxLength(3)
    @yascFormSubsection('projectBudgetSectionBillingModel')
    @yascFormIndex(21)
    @yascFormGrid({width: 1})
    currencyCode = 'EUR';

    @yascField()
    @yascDisplayName('projectBudgetStart')
    @yascDescription('projectBudgetStartHint')
    @yascTableIgnore(true)
    @yascFormKind('date')
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionTerm')
    @yascFormIndex(30)
    @yascFormGrid({width: 1})
    start?: string;

    @yascField()
    @yascDisplayName('projectBudgetEnd')
    @yascDescription('projectBudgetEndHint')
    @yascTableIgnore(true)
    @yascFormKind('date')
    @yascFormSubsection('projectBudgetSectionTerm')
    @yascFormIndex(31)
    @yascFormGrid({width: 1})
    end?: string;

    @yascField()
    @yascDisplayName('projectBudgetDefaultHourlyRate')
    @yascDescription('projectBudgetDefaultHourlyRateHint')
    @yascTableIgnore(true)
    @yascMin(0.0001)
    @yascMax(1000000000)
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionConditions')
    @yascFormIndex(40)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetUsesModel(draft, BudgetPricingModel.TimeAndMaterial, BudgetPricingModel.CappedTimeAndMaterial))
    defaultHourlyRate: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetFixedPrice')
    @yascDescription('projectBudgetFixedPriceHint')
    @yascTableIgnore(true)
    @yascMin(0.01)
    @yascMax(1000000000)
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionConditions')
    @yascFormIndex(41)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetUsesModel(draft, BudgetPricingModel.FixedPrice, BudgetPricingModel.PrepaidHours, BudgetPricingModel.Retainer))
    fixedPrice: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetAmountLimit')
    @yascDescription('projectBudgetAmountLimitHint')
    @yascTableIgnore(true)
    @yascMin(0.01)
    @yascMax(1000000000)
    @yascFormSubsection('projectBudgetSectionConditions')
    @yascFormIndex(42)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetUsesModel(draft, BudgetPricingModel.CappedTimeAndMaterial))
    amountLimit: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetIncludedHours')
    @yascDescription('projectBudgetIncludedHoursHint')
    @yascTableIgnore(true)
    @yascMin(0.001)
    @yascMax(1000000000)
    @yascFormSubsection('projectBudgetSectionConditions')
    @yascFormIndex(43)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetUsesModel(draft, BudgetPricingModel.CappedTimeAndMaterial, BudgetPricingModel.PrepaidHours, BudgetPricingModel.Retainer))
    includedHours: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetPlannedHours')
    @yascDescription('projectBudgetPlannedHoursHint')
    @yascTableIgnore(true)
    @yascMin(0.001)
    @yascMax(1000000000)
    @yascFormSubsection('projectBudgetSectionConditions')
    @yascFormIndex(44)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetUsesModel(draft, BudgetPricingModel.TimeAndMaterial, BudgetPricingModel.FixedPrice))
    plannedHours: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetCapacityUnit')
    @yascEnum(BudgetCapacityUnit, 'projectBudgetCapacityUnitEnum')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(45)
    @yascFormGrid({width: 1})
    capacityUnit: BudgetCapacityUnit = BudgetCapacityUnit.Hours;

    @yascField()
    @yascDisplayName('projectBudgetCapacityInterval')
    @yascEnum(BudgetCapacityInterval, 'projectBudgetCapacityIntervalEnum')
    @yascDescription('projectBudgetCapacityIntervalHint')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(46)
    @yascFormGrid({width: 1})
    capacityInterval: BudgetCapacityInterval = BudgetCapacityInterval.None;

    @yascField()
    @yascDisplayName('projectBudgetCapacityAmount')
    @yascDescription('projectBudgetCapacityAmountHint')
    @yascTableIgnore(true)
    @yascMin(0.001)
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(62)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => !!draft.capacityInterval)
    capacityAmount: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetHoursPerDay')
    @yascDescription('projectBudgetHoursPerDayHint')
    @yascTableIgnore(true)
    @yascMin(0.25)
    @yascMax(24)
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(47)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => draft?.capacityUnit === BudgetCapacityUnit.Days)
    hoursPerDay = 8;

    @yascField()
    @yascDisplayName('projectBudgetConsumptionFactor')
    @yascDescription('projectBudgetConsumptionFactorHint')
    @yascTableIgnore(true)
    @yascMin(0.001)
    @yascMax(10)
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(48)
    @yascFormGrid({width: 1})
    consumptionFactor = 1;

    @yascField()
    @yascDisplayName('projectBudgetManualOnly')
    @yascDescription('projectBudgetManualOnlyHint')
    @yascTableIgnore(true)
    @yascFormKind('checkbox')
    @yascFormSubsection('projectBudgetSectionCapacity')
    @yascFormIndex(49)
    @yascFormGrid({width: 2, fullRow: true})
    manualOnly = false;

    @yascField()
    @yascDisplayName('projectBudgetRecurrenceType')
    @yascEnum(BudgetRecurrenceType, 'projectBudgetRecurrenceTypeEnum')
    @yascDescription('projectBudgetRecurrenceTypeHint')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionRecurrence')
    @yascFormIndex(50)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetSupportsRecurrence(draft))
    recurrenceType: BudgetRecurrenceType = BudgetRecurrenceType.None;

    @yascField()
    @yascDisplayName('projectBudgetRecurrenceInterval')
    @yascDescription('projectBudgetRecurrenceIntervalHint')
    @yascTableIgnore(true)
    @yascMin(1)
    @yascFormSubsection('projectBudgetSectionRecurrence')
    @yascFormIndex(51)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetSupportsRecurrence(draft) && draft?.recurrenceType === BudgetRecurrenceType.Custom)
    recurrenceInterval: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetCarryOverType')
    @yascEnum(BudgetCarryOverType, 'projectBudgetCarryOverTypeEnum')
    @yascDescription('projectBudgetCarryOverTypeHint')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascFormSubsection('projectBudgetSectionCarryOver')
    @yascFormIndex(60)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetHasCapacityInterval(draft))
    carryOverType: BudgetCarryOverType = BudgetCarryOverType.None;

    @yascField()
    @yascDisplayName('projectBudgetCarryOverLimitHours')
    @yascDescription('projectBudgetCarryOverLimitHoursHint')
    @yascTableIgnore(true)
    @yascMin(0)
    @yascFormSubsection('projectBudgetSectionCarryOver')
    @yascFormIndex(61)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetHasCapacityInterval(draft)
        && draft?.carryOverType === BudgetCarryOverType.Limited
       )
    carryOverLimitHours: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetCarryOverLimitAmount')
    @yascDescription('projectBudgetCarryOverLimitAmountHint')
    @yascTableIgnore(true)
    @yascMin(0)
    @yascFormSubsection('projectBudgetSectionCarryOver')
    @yascFormIndex(62)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetHasCapacityInterval(draft) && draft?.carryOverType === BudgetCarryOverType.Limited)
    carryOverLimitAmount: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetCarryOverPercentage')
    @yascDescription('projectBudgetCarryOverPercentageHint')
    @yascTableIgnore(true)
    @yascMin(0)
    @yascMax(100)
    @yascFormSubsection('projectBudgetSectionCarryOver')
    @yascFormIndex(63)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetHasCapacityInterval(draft) && draft?.carryOverType === BudgetCarryOverType.Percentage)
    carryOverPercentage: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetCarryOverExpirationPeriods')
    @yascDescription('projectBudgetCarryOverExpirationPeriodsHint')
    @yascTableIgnore(true)
    @yascMin(1)
    @yascFormSubsection('projectBudgetSectionCarryOver')
    @yascFormIndex(64)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetHasCapacityInterval(draft) && draft?.carryOverType !== BudgetCarryOverType.None)
    carryOverExpirationPeriods: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetOveragePolicy')
    @yascEnum(BudgetOveragePolicy, 'projectBudgetOveragePolicyEnum')
    @yascDescription('projectBudgetOveragePolicyHint')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascRequired()
    @yascFormSubsection('projectBudgetSectionOverage')
    @yascFormIndex(70)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetSupportsOverage(draft))
    overagePolicy: BudgetOveragePolicy = BudgetOveragePolicy.RequiresApproval;

    @yascField()
    @yascDisplayName('projectBudgetOverageHourlyRate')
    @yascDescription('projectBudgetOverageHourlyRateHint')
    @yascTableIgnore(true)
    @yascMin(0.0001)
    @yascFormSubsection('projectBudgetSectionOverage')
    @yascFormIndex(71)
    @yascFormGrid({width: 1})
    @yascVisibleWhen(({draft}) => budgetSupportsOverage(draft) && draft?.overagePolicy === BudgetOveragePolicy.Billable)
    overageHourlyRate: number | null = null;

    @yascField()
    @yascDisplayName('projectBudgetTotalHours')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascTableSuffix(' h')
    @yascTableWidth(100)
    @yascFormIgnore()
    totalHours = 0;

    @yascField()
    @yascDisplayName('projectBudgetHourlyRate')
    @yascTableIgnore(true)
    @yascFormIgnore()
    hourlyRate = 0;

    @yascField()
    @yascDisplayName('projectBudgetReference')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    @yascFormSubsection('projectBudgetSectionAdditional')
    @yascFormIndex(80)
    @yascFormGrid({width: 1})
    reference = '';

    @yascField()
    @yascDisplayName('projectBudgetActualWorkedHours')
    @yascTableIgnore(true)
    @yascFormIgnore()
    actualWorkedHours = 0;

    @yascField()
    @yascDisplayName('projectBudgetOperationalRemainingHours')
    @yascTableIgnore(true)
    @yascFormIgnore()
    operationalRemainingHours = 0;

    @yascField()
    @yascDisplayName('projectBudgetUsedHours')
    @yascTableIgnore(true)
    @yascFormIgnore()
    usedHours = 0;

    @yascField()
    @yascDisplayName('projectBudgetRemainingHours')
    @yascTableIgnore(true)
    @yascFormIgnore()
    remainingHours = 0;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    invoicedHours = 0;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    unbilledWorkedHours = 0;

    @yascField()
    @yascDisplayName('projectBudgetTotalNetto')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascTableSuffix(' €')
    @yascTableWidth(120)
    @yascFormIgnore()
    totalNetto = 0;

    @yascField()
    @yascDisplayName('projectBudgetRemainingNetto')
    @yascTableIgnore(true)
    @yascFormIgnore()
    remainingNetto = 0;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    usedNetto = 0;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    actualNetto = 0;

    @yascField()
    @yascTableIgnore(true)
    @yascFormIgnore()
    operationalRemainingNetto = 0;

    @yascField()
    @yascDisplayName('projectBudgetSourceOffer')
    @yascTableIgnore(true)
    @yascFormIgnore()
    sourceOfferNumber = '';

    @yascField()
    @yascDisplayName('general_description')
    @yascTableIgnore(true)
    @yascFormKind('textarea')
    @yascFormRows(3)
    @yascMaxLength(500)
    @yascFormSubsection('projectBudgetSectionAdditional')
    @yascFormIndex(82)
    @yascFormGrid({width: 2, fullRow: true})
    comment = '';

    user?: {id?: number; givenName?: string; lastName?: string; userName?: string; email?: string; fullName?: string};
    userName?: string;

    constructor(init: Partial<ProjectBudgetItem | ProjectBudgetRes> = {}) {
        Object.assign(this, init);
        this.clientId = (init as any).clientId ?? (this.id ? `budget-${this.id}` : (globalThis.crypto?.randomUUID?.() ?? `budget-${Date.now()}-${Math.random()}`));
        this.userId = (init as any).userId ?? (init as any).user?.id ?? null;
        this.user = (init as any).user;
        this.userName = [(init as any).user?.givenName, (init as any).user?.lastName].filter(Boolean).join(' ').trim()
            || (init as any).user?.fullName
            || (init as any).user?.email;
        this.start = (init as any).start?.slice?.(0, 10) ?? (init as any).start;
        this.end = (init as any).end?.slice?.(0, 10) ?? (init as any).end ?? undefined;
        this.pricingModel = (init as any).pricingModel ?? BudgetPricingModel.TimeAndMaterial;
        this.currencyCode = (init as any).currencyCode ?? 'EUR';
        this.plannedHours = Object.prototype.hasOwnProperty.call(init, 'plannedHours') ? ((init as any).plannedHours ?? null) : ((init as any).totalHours ?? null);
        this.includedHours = Object.prototype.hasOwnProperty.call(init, 'includedHours') ? ((init as any).includedHours ?? null) : ((init as any).totalHours ?? null);
        this.defaultHourlyRate = Object.prototype.hasOwnProperty.call(init, 'defaultHourlyRate') ? ((init as any).defaultHourlyRate ?? null) : ((init as any).hourlyRate ?? null);
        this.recurrenceType = (init as any).recurrenceType ?? BudgetRecurrenceType.None;
        this.carryOverType = (init as any).carryOverType ?? BudgetCarryOverType.None;
        this.overagePolicy = (init as any).overagePolicy ?? BudgetOveragePolicy.RequiresApproval;
    }
}

@yascClassDisplayName('projectTableTask')
@yascClassFormSectionTitle('projectTableTask')
export class ProjectTaskItem {
    @yascField()
    @yascDisplayName('crm_task_id_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id = '';

    @yascField()
    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(260)
    @yascFormIgnore()
    name = '';

    @yascField()
    @yascDisplayName('crm_contract_category_label')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascFormIgnore()
    category?: string;

    @yascField()
    @yascDisplayName('crm_task_currentStart_label')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    currentStart?: string;

    @yascField()
    @yascDisplayName('crm_task_currentEnd_label')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    currentEnd?: string;

    @yascField()
    @yascDisplayName('crm_task_currentEst_label')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Number)
    @yascFormIgnore()
    currentEstimated?: number;

    constructor(init: Partial<ProjectTaskItem | BaseTaskRes> = {}) {
        Object.assign(this, init);
        this.id = `${(init as any).id ?? ''}`;
        this.name = (init as any).name ?? '';
    }
}

@yascClassDisplayName('projectTabInvoicesTitle')
@yascClassFormSectionTitle('projectTabInvoicesTitle')
export class ProjectInvoiceItem {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;

    @yascField()
    @yascDisplayName('invoiceNumber')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascFormIgnore()
    invoiceNumber = '';

    @yascField()
    @yascDisplayName('general_createdAt')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    created?: string;

    @yascField()
    @yascDisplayName('invoicePaymentTarget')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    paymentTarget?: string;

    @yascField()
    @yascDisplayName('invoicePaidOn')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    paidOn?: string;

    @yascField()
    @yascDisplayName('projectTableVolume')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascFormIgnore()
    totalBrutto?: number;

    constructor(init: Partial<ProjectInvoiceItem | InvoiceDetailRes> = {}) {
        Object.assign(this, init);
        this.invoiceNumber = (init as any).invoiceNumber ?? '';
    }
}

@yascClassDefaultFilters([{
    operator: LogicalOperator.And,
    conditions: [{fieldName: 'currentWorkflowStatus', value: PROJECT_ACTIVE_STATUS_NAME, condition: FieldOperator.equal}],
}])
@yascClassDefaultSorting([{fieldName: 'customer', direction: 'asc', sortIndex: 0}, {fieldName: 'name', direction: 'asc', sortIndex: 1}])
@yascEntityName('general_project')
@yascClassDisplayName('general_project')
@yascClassFormSectionTitle('projectTabGeneralTitle')
@yascClassSectionOrderStrategy(YascSectionOrderStrategy.ExplicitThenTitleAsc)
export class Project {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIndex(99)
    @yascTableColumn(ColumnType.Number)
    @yascTableHidden(true)
    @yascFormIgnore()
    id = 0;

    @yascField()
    @yascDisplayName('general_name')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(260)
    @yascRequired()
    @yascMaxLength(150)
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 1, width: 1})
    name = '';

    @yascField()
    @yascDisplayName('general_internalName')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 1, width: 1})
    internalName = '';

    @yascField()
    @yascDisplayName('projectTableCustomer')
    @yascTableIndex(0)
    @yascTableWidth(4)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayCompany)
    @yascFormIgnore()
    customer: CompanyMinimumRes | null = null;

    @yascField()
    @yascDisplayName('projectTableCustomer')
    @yascTableIgnore(true)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectCustomers')
    @yascRequired()
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 2, width: 1})
    customerId: number | null = null;

    @yascField()
    @yascDisplayName('projectTimesheetCustomer')
    @yascTableIgnore(true)
    @yascFormIgnore()
    timesheetCustomer: CompanyMinimumRes | null = null;

    @yascField()
    @yascDisplayName('projectTimesheetCustomer')
    @yascTableIgnore(true)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectCustomers')
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 2, width: 1})
    timesheetCustomerId: number | null = null;

    @yascField()
    @yascDisplayName('general_currentWorkflow')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('projectWorkflows')
    @yascRequired()
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 3, width: 1})
    workflowId: number | null = null;

    @yascField()
    @yascDisplayName('general_status')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('projectWorkflowStatuses')
    @yascRequired()
    @yascFormSubsection('projectFormSectionGeneral')
    @yascFormGrid({row: 3, width: 1})
    currentStatusId: number | null = null;

    @yascField()
    @yascDisplayName('general_status')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayWorkflowStatus)
    @yascTableFilterType(FilterType.select)
    @yascFilterConfig({
        getGroup: (value?: {availableWorkflows?: WorkflowStatusMinRes[]}) => value?.availableWorkflows ?? [],
        mapToFilter: (status: WorkflowStatusMinRes) => new YascOptionItem<string>(status.name, status.name),
        multiselect: true,
    })
    @yascFormIgnore()
    currentWorkflowStatus?: WorkflowStatusMinRes;

    @yascField()
    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Text)
    @yascMaxLength(100)
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 4, width: 1})
    orderNr = '';

    @yascField()
    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(5)
    @yascTableColumn(ColumnType.Text)
    @yascMaxLength(100)
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 4, width: 1})
    offerNr = '';

    @yascField()
    @yascDisplayName('billingProjectPaymentTermsDays')
    @yascTableIgnore(true)
    @yascFormKind('number')
    @yascFormTooltip('billingProjectPaymentTermsHint')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 5, width: 1})
    billingPaymentTermsDays: number | null = null;

    @yascField()
    @yascDisplayName('billingDefaultDiscountPercent')
    @yascTableIgnore(true)
    @yascFormKind('number')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 5, width: 1})
    billingPaymentDiscountPercent: number | null = null;

    @yascField()
    @yascDisplayName('billingDefaultDiscountDays')
    @yascTableIgnore(true)
    @yascFormKind('number')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 6, width: 1})
    billingPaymentDiscountDays: number | null = null;

    @yascField()
    @yascDisplayName('billingPreferredInvoiceFormat')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('invoiceFormats')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 6, width: 1})
    billingPreferredInvoiceFormat: InvoiceDocumentFormat | null = null;

    @yascField()
    @yascDisplayName('timesheetTemplate')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('timesheetTemplates')
    @yascFormTooltip('timesheetTemplateProjectHint')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 7, width: 1})
    timesheetTemplate: TimesheetTemplate | null = null;

    @yascField()
    @yascDisplayName('timesheetSignee')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('timesheetSignees')
    @yascFormTooltip('timesheetSigneeProjectHint')
    @yascFormSubsection('projectFormSectionBilling')
    @yascFormGrid({row: 7, width: 1})
    timesheetSignee: TimesheetSignee | null = null;

    @yascField()
    @yascDisplayName('projectTableVolume')
    @yascTableIndex(6)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascFormIgnore()
    projectVolumeBrutto = 0;

    @yascField()
    @yascDisplayName('projectTableRemainingAmount')
    @yascTableIndex(7)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascFormIgnore()
    projectVolumeBruttoOpen = 0;

    @yascField()
    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(8)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormSubsection('projectFormSectionPlanning')
    @yascFormGrid({row: 7, width: 1})
    nextBill?: string;

    @yascField()
    @yascDisplayName('projectTableStart')
    @yascTableIndex(9)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormSubsection('projectFormSectionPlanning')
    @yascFormGrid({row: 8, width: 1})
    start?: string;

    @yascField()
    @yascDisplayName('projectTableEnd')
    @yascTableIndex(10)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormSubsection('projectFormSectionPlanning')
    @yascFormGrid({row: 8, width: 1})
    end?: string;

    @yascField()
    @yascDisplayName('projectTabTeamTitle')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Number)
    @yascFormIgnore()
    teamMemberCount = 0;

    @yascField()
    @yascDisplayName('projectRequireTasks')
    @yascTableIgnore()
    @yascFormKind('checkbox')
    @yascFormSubsection('crm.project.formSections.timeTrackingEntrySettings')
    @yascFormGrid({row: 9, width: 1})
    requireTasks = false;


    @yascDisplayName('crm.project.form.requireEfforts')
    @yascTableIgnore()
    @yascFormKind('checkbox')
    @yascFormSubsection('crm.project.formSections.timeTrackingEntrySettings')
    @yascFormGrid({row: 9, width: 1, column:2})
    requireEfforts = false;

    @yascField()
    @yascDisplayName('projectRequireProjectBudget')
    @yascTableIgnore(true)
    @yascFormKind('checkbox')
    @yascFormSubsection('projectFormSectionSettings')
    @yascFormGrid({row: 9, width: 1})
    requireProjectBudget = false;

    @yascField()
    @yascDisplayName('general_color')
    @yascTableIgnore(true)
    @yascMaxLength(7)
    @yascFormSubsection('projectFormSectionSettings')
    @yascFormGrid({row: 10, width: 1})
    color?: string;

    @yascField()
    @yascDisplayName('general_description')
    @yascTableIgnore(true)
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascMaxLength(500)
    @yascFormSubsection('projectFormSectionSettings')
    @yascFormGrid({row: 11, width: 2, fullRow: true})
    comment = '';

    @yascField()
    @yascDisplayName('projectTabTeamTitle')
    @yascSectionOrder(10)
    @yascNested(ProjectTeamMember)
    @yascCollection({
        itemIdKey: 'userId',
        createItem: () => new ProjectTeamMember(),
    })
    teamMembers: ProjectTeamMember[] = [];

    @yascField()
    @yascDisplayName('projectContractsTitle')
    @yascSectionOrder(20)
    @yascNested(ProjectContractItem)
    @yascCollection({
        itemIdKey: 'clientId',
        editOnRowClick: true,
        createItem: () => new ProjectContractItem({
            status: ProjectContractStatus.Draft,
        }),
    })
    contracts: ProjectContractItem[] = [];

    @yascField()
    @yascDisplayName('projectBudgetTitle')
    @yascSectionOrder(30)
    @yascNested(ProjectBudgetItem)
    @yascCollection({
        itemIdKey: 'clientId',
        editOnRowClick: true,
        createItem: () => new ProjectBudgetItem({
            name: '',
            pricingModel: BudgetPricingModel.TimeAndMaterial,
            currencyCode: 'EUR',
            recurrenceType: BudgetRecurrenceType.None,
            carryOverType: BudgetCarryOverType.None,
            overagePolicy: BudgetOveragePolicy.RequiresApproval,
            isActive: true,
        }),
    })
    budgets: ProjectBudgetItem[] = [];

    @yascField()
    @yascDisplayName('projectTableTask')
    @yascSectionOrder(40)
    @yascNested(ProjectTaskItem)
    @yascCollection({
        itemIdKey: 'id',
        readOnly: true,
        createItem: () => new ProjectTaskItem(),
    })
    tasks: ProjectTaskItem[] = [];

    @yascField()
    @yascDisplayName('projectTabInvoicesTitle')
    @yascSectionOrder(50)
    @yascNested(ProjectInvoiceItem)
    @yascCollection({
        itemIdKey: 'id',
        readOnly: true,
        createItem: () => new ProjectInvoiceItem(),
    })
    invoices: ProjectInvoiceItem[] = [];

    constructor(init: Partial<Project> = {}) {
        Object.assign(this, init);
        this.id = init.id ?? 0;
        this.name = init.name ?? '';
        this.internalName = init.internalName ?? '';
        this.customer = init.customer ?? null;
        this.customerId = init.customerId ?? init.customer?.id ?? null;
        this.timesheetCustomer = init.timesheetCustomer ?? null;
        this.timesheetCustomerId = init.timesheetCustomerId ?? init.timesheetCustomer?.id ?? null;
        this.workflowId = init.workflowId ?? null;
        this.currentWorkflowStatus = init.currentWorkflowStatus;
        this.currentStatusId = init.currentStatusId ?? init.currentWorkflowStatus?.id ?? null;
        this.orderNr = init.orderNr ?? '';
        this.offerNr = init.offerNr ?? '';
        this.comment = init.comment ?? '';
        this.projectVolumeBrutto = init.projectVolumeBrutto ?? 0;
        this.projectVolumeBruttoOpen = init.projectVolumeBruttoOpen ?? 0;
        this.teamMemberCount = init.teamMemberCount ?? init.teamMembers?.length ?? 0;
        this.requireTasks = init.requireTasks ?? false;
        this.requireProjectBudget = init.requireProjectBudget ?? false;
        this.teamMembers = (init.teamMembers ?? []).map(x => new ProjectTeamMember(x));
        this.contracts = (init.contracts ?? []).map(x => new ProjectContractItem(x));
        this.budgets = (init.budgets ?? []).map(x => new ProjectBudgetItem(x));
        this.tasks = (init.tasks ?? []).map(x => new ProjectTaskItem(x));
        this.invoices = (init.invoices ?? []).map(x => new ProjectInvoiceItem(x));
    }

    clone(changes: Partial<Project> = {}): Project {
        return new Project({
            ...this,
            ...changes,
            customer: changes.customer ?? this.customer,
            customerId: changes.customerId ?? this.customerId,
            timesheetCustomer: changes.timesheetCustomer ?? this.timesheetCustomer,
            timesheetCustomerId: changes.timesheetCustomerId !== undefined ? changes.timesheetCustomerId : this.timesheetCustomerId,
            currentWorkflowStatus: changes.currentWorkflowStatus ?? this.currentWorkflowStatus,
            teamMembers: changes.teamMembers ?? this.teamMembers,
            contracts: changes.contracts ?? this.contracts,
            budgets: changes.budgets ?? this.budgets,
            tasks: changes.tasks ?? this.tasks,
            invoices: changes.invoices ?? this.invoices,
        });
    }
}
